/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// RUN 8: Figure & Caption Styling
// ═══════════════════════════════════════════════════════════════════════════

import { reportResult, paraDetail, RunDetail } from '../runs/common';
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig } from '../taskpane/configService';

// ── Detection helpers ──

function isFigureCaption(text: string, figureRegex: RegExp): boolean {
  if (!text?.trim()) return false;
  const clean = text.trim().replace(/[\u200B-\u200D\uFEFF]/g, "");
  return figureRegex.test(clean);
}

/** LOF entries have a TAB followed by a page number at end */
function isLOFEntry(text: string): boolean {
  if (!text?.includes('\t')) return false;
  return /\t[\s.]*\d+\s*$/.test(text);
}

function isLOFHeading(text: string): boolean {
  if (!text) return false;
  const t = text.trim().toUpperCase();
  return t === 'LIST OF FIGURES' ||
    t === 'LIST OF IN-TEXT FIGURES' ||
    t === 'LIST OF IN TEXT FIGURES' ||
    t.startsWith('LIST OF FIGURES') ||
    t.startsWith('LIST OF IN-TEXT FIGURES') ||
    t.startsWith('LIST OF IN TEXT FIGURES') ||
    t === 'LOF' ||
    t === 'TABLE OF FIGURES';
}

// ─── Style filter ──────────────────────────────────────────────────────
//
// IMPORTANT: this filter is intentionally narrow. The whole point of Run 8
// is to CORRECT figure captions that were authored with the wrong paragraph
// style — common offenders include:
//   • Caption Table              (caption author picked the table caption style)
//   • Footnote Text / Footnote Figure / Footnote Table
//   • Heading 1/2/3, n_Heading_*, "Unnumbered Heading"
//   • Normal, Body Text, List Paragraph, BodyTextIndent
//
// All of those need to be fixable. So this filter does NOT skip paragraphs
// based on their current style. Instead, the text predicate (`isFigureCaption`)
// drives the decision — a paragraph is treated as a figure caption iff its
// TEXT matches `^Figure \d+`. Style is only used to skip a no-op when the
// paragraph is already correct.
//
// We retain only TWO narrow style-based skips:
//   1. paragraphs already wearing the target caption style — no-op, don't
//      need to write to them and we shouldn't double-count them
//   2. TOC / LOF entries — these match `^Figure \d+` but are not real
//      captions; they are caught by `isLOFEntry` (text-based, looks for
//      tab + page-number tail) and by the `insideLOFSection` walker.
//
// The previous version had a broad SKIP_STYLES substring match that filtered
// out 'heading', 'caption table', 'footnote', etc. before the caption test
// even ran — that was the source of the "won't fix mis-styled captions" bug.

const TOC_LOF_STYLE_PATTERNS = [
  'toc', 'table of contents', 'tableofcontents',
  'lof', 'list of figures', 'lofhead', 'table of figures',
  'list of in-text figures', 'list of in text figures',
];

function isTOCorLOFStyle(styleName: string): boolean {
  if (!styleName) return false;
  const l = styleName.toLowerCase().trim().replace(/\s+/g, ' ');
  return TOC_LOF_STYLE_PATTERNS.some(p => l.includes(p));
}

/** Result of walking the LOF-section state machine for one paragraph. */
interface LOFState {
  /** The (possibly updated) "inside LOF section" flag for the next iteration. */
  insideLOFSection: boolean;
  /** True if this paragraph should be skipped (no caption processing). */
  skip: boolean;
  /** True if this paragraph is the LOF heading that opened the section. */
  enteredLOF: boolean;
}

/** Pure LOF-section state machine. Given the current paragraph's text/style
 *  and whether we are already inside the LOF section, decide the new state
 *  and whether the caller should skip this paragraph.
 *
 *  Note: when we reach the `insideLOFSection` branch, `isLOFHeading(text)` is
 *  already known to be false (the first guard returns early), so the original
 *  `&& !isLOFHeading(t)` check is redundant and dropped here. */
function updateLOFSection(text: string, style: string, insideLOFSection: boolean): LOFState {
  if (isLOFHeading(text)) {
    return { insideLOFSection: true, skip: true, enteredLOF: true };
  }
  if (insideLOFSection) {
    // Exit the LOF section as soon as we see a real heading, and let that
    // paragraph fall through to the caption checks. Otherwise keep skipping.
    const isRealHeading = style.toLowerCase().includes('heading');
    return { insideLOFSection: !isRealHeading, skip: !isRealHeading, enteredLOF: false };
  }
  return { insideLOFSection, skip: false, enteredLOF: false };
}

/** Pure text/style predicate: is this a fixable figure caption?
 *  Assumes table-cell and LOF-section paragraphs have already been filtered
 *  out by the caller. */
function isFixableCaption(
  text: string,
  style: string,
  captionStyle: string,
  figureRegex: RegExp
): boolean {
  if (!text.trim()) return false;
  // Text-driven gate: is this paragraph a figure caption? (regex like
  // /^Figure\s+\d/i — applied to the trimmed text)
  if (!isFigureCaption(text, figureRegex)) return false;
  // Skip TOC/LOF entries that happen to start with "Figure N" but are really
  // table-of-figures rows: text-based (tab + page-number tail) and style-based.
  if (isLOFEntry(text)) return false;
  if (isTOCorLOFStyle(style)) return false;
  // Skip when already correct — saves a round-trip and avoids a spurious
  // tracked-change marker.
  if (style === captionStyle) return false;
  return true;
}

/** Scan body paragraphs for figure captions, skipping LOF section */
async function scanBodyForFigureCaptions(
  context: Word.RequestContext,
  captionStyle: string,
  figureRegex: RegExp,
  changedParas: RunDetail[]
): Promise<number> {
  let captionsStyled = 0;

  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();

  let insideLOFSection = false;

  for (let i = 0; i < paragraphs.items.length; i++) {
    const para = paragraphs.items[i];
    para.load("text, style, uniqueLocalId");

    // Per the user's requirement, Run 8 must NOT touch any figure-related
    // content inside a table. body.paragraphs sometimes includes table-cell
    // paragraphs (Word.js 1.4+) and sometimes excludes them — checking the
    // parent table explicitly works on every version.
    const parentTable = para.parentTableOrNullObject;
    parentTable.load("isNullObject");
    await context.sync();
    if (!parentTable.isNullObject) continue;

    const t = para.text || "";
    const s = para.style || "";

    // LOF-section state machine (enter on LOF heading, exit on next real heading).
    const lof = updateLOFSection(t, s, insideLOFSection);
    insideLOFSection = lof.insideLOFSection;
    if (lof.enteredLOF) {
      console.log(`[Body ${i + 1}] LOF HEADING detected — entering LOF section`);
    }
    if (lof.skip) continue;

    // Everything that survives gets fixed if its TEXT matches `^Figure \d+`
    // and it's not a TOC/LOF entry — Caption Table, Footnote Text, Heading 3,
    // Unnumbered Heading, Normal, Body Text, List Paragraph, anything else.
    if (!isFixableCaption(t, s, captionStyle, figureRegex)) continue;

    console.log(`[Body ${i + 1}] CAPTION (was "${s}"): "${t.trim().substring(0, 60)}" \u2192 "${captionStyle}"`);
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const uid = para.uniqueLocalId;  // captured before style write
    const txt = t.trim();
    para.style = captionStyle;
    await context.sync();
    changedParas.push(paraDetail(uid, txt, `was "${s}" \u2192 ${captionStyle}`));
    captionsStyled++;
  }

  return captionsStyled;
}

// NOTE: there used to be a `scanTablesForFigureCaptions` function here that
// walked every table cell and restyled paragraphs whose text matched
// `^Figure \d+`. It was removed when the user clarified that Run 8 must
// NOT modify any figure-related content inside a table. Captions inside
// tables are out of Run 8's scope. The body scan above also has a parent-
// table guard, and `styleInlinePictures` below has a matching guard, so
// figures inside tables are uniformly left alone now.

/** Style inline pictures as Body Text + correct alignment */
async function styleInlinePictures(
  context: Word.RequestContext,
  bodyTextStyle: string
): Promise<{ figuresStyled: number; figuresAligned: number }> {
  let figuresStyled = 0;
  let figuresAligned = 0;

  const pics = context.document.body.inlinePictures;
  pics.load("items");
  await context.sync();

  console.log(`Total inline figures: ${pics.items.length}\n`);

  for (let i = 0; i < pics.items.length; i++) {
    const picture = pics.items[i];
    const figurePara = picture.paragraph;

    // Skip pictures inside table cells — Run 8 must not touch anything
    // inside a table. body.inlinePictures returns ALL inline pictures
    // (including those embedded in table cells), so the guard has to be
    // explicit here.
    const parentTable = figurePara.parentTableOrNullObject;
    parentTable.load("isNullObject");
    figurePara.load("style, alignment");
    await context.sync();

    if (!parentTable.isNullObject) {
      console.log(`[Figure ${i + 1}] inside a table — skipped (Run 8 leaves table figures alone)`);
      continue;
    }

    if (figurePara.style !== bodyTextStyle) {
      console.log(`[Figure ${i + 1}] Style: "${figurePara.style}" \u2192 "${bodyTextStyle}"`);
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      figurePara.style = bodyTextStyle;
      await context.sync();
      figuresStyled++;
    }

    try {
      figurePara.load("alignment");
      await context.sync();
      if (figurePara.alignment !== "Left" && figurePara.alignment !== "Centered") {
        context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
        figurePara.alignment = Word.Alignment.left;
        await context.sync();
        figuresAligned++;
      }
    } catch (e) {
      console.log(`[Figure ${i + 1}] Could not set alignment: ${e.message}`);
    }
  }

  return { figuresStyled, figuresAligned };
}

export async function run8() {
  const cfg = getConfig();
  const fCfg = cfg.run8_figures;
  const captionStyle = fCfg.captionStyle;
  const bodyTextStyle = fCfg.bodyTextStyle;
  const figureRegex = new RegExp(fCfg.figureRegex, "i");

  await Word.run(async (context) => {
    try {
      await ensureRunStyles(context, 8);
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const changedParas: RunDetail[] = [];

      console.log("=".repeat(70));
      console.log(`=== FIGURE & CAPTION STYLING CHECKER (${cfg.clientName}) ===`);
      console.log("=".repeat(70));

      console.log("\n\u2500\u2500 Scanning body paragraphs for figure captions \u2500\u2500\n");
      const captionsStyled = await scanBodyForFigureCaptions(context, captionStyle, figureRegex, changedParas);

      // The previous version also walked every table cell looking for
      // caption text — that pass has been removed. Per the regulatory
      // standard, Run 8 must not modify any figure-related content inside
      // a table. The body scan above guards against table-cell paragraphs,
      // and `styleInlinePictures` below skips pictures inside tables.

      console.log("\n\u2500\u2500 Styling figures: Body Text + inline with text (skipping table figures) \u2500\u2500\n");
      const { figuresStyled, figuresAligned } = await styleInlinePictures(context, bodyTextStyle);

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log("\n" + "=".repeat(70));
      console.log(`Captions styled: ${captionsStyled} | Figures styled: ${figuresStyled} | Aligned: ${figuresAligned}`);
      console.log("✓ Figure & Caption styling complete!");

      const total = captionsStyled + figuresStyled + figuresAligned;
      if (total > 0) {
        reportResult('Figure & Caption Styling Complete', [
          { label: 'Captions found (body only)', count: captionsStyled },
          { label: 'Figures styled (body only)', count: figuresStyled },
          { label: 'Figures aligned',            count: figuresAligned },
          ...changedParas,
        ]);
      } else {
        reportResult('Figure & Caption Styling — no changes needed', [{ label: 'All figures already correct', note: 'No changes made' }]);
      }
    } catch (error) {
      console.error("Error in run8:", error);
      console.error("Message:", error.message);
    }
  });
}
